import { LinkedItem } from '@types'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import {
  composeMatrixModuleOnRinconeroLeg,
  composeModularCornerOnUnitDrawMatrix,
  composeStraightModuleSnapOnRinconero,
  defaultThicknessForDrawCatalog,
  defaultWidthForDrawCatalog,
  isModularCornerSnapCatalog,
  isRinconeroCatalog,
} from '@utils/sceneObjects/items/rinconeroUtils'
import { Matrix4 } from 'three'

export function getRelativeMatrixOLD(referenceMatrix: Matrix4, offset: number) {
  const newMatrix = referenceMatrix.clone()

  const position = toVector3()
  const quaternion = toQuaternion()
  const scale = toVector3()
  newMatrix.decompose(position, quaternion, scale)

  const offsetVector = toVector3([offset, 0, 0]).applyQuaternion(quaternion.normalize())

  position.add(offsetVector)

  newMatrix.compose(position, quaternion, scale)

  return newMatrix
}

/**
 * Matriz relativa al dibujar.
 * Rinconero → módulo recto (160 / RELAX / …): un solo camino —
 * composeStraightModuleSnapOnRinconero (pata L o lateral según snapSide).
 */
export function getRelativeMatrix(
  referenceMatrix: Matrix4,
  offset: number,
  referenceItem?: LinkedItem,
  heightOffset?: number,
  _newItemTipoNormalizado?: string,
  drawingCatalogID?: string,
  snapSide?: 'left' | 'right',
  moduleWidth = 0,
  moduleThickness = 0,
) {
  const newMatrix = referenceMatrix.clone()

  const position = toVector3()
  const quaternion = toQuaternion()
  const scale = toVector3()
  newMatrix.decompose(position, quaternion, scale)

  const refCatalog = referenceItem?.catalogID
  const drawingCatalog = drawingCatalogID
  const refIsRinconero = isModularCornerSnapCatalog(refCatalog)
  const drawingIsRinconero = isModularCornerSnapCatalog(drawingCatalog)
  const refUnit = referenceItem as Parameters<typeof composeMatrixModuleOnRinconeroLeg>[0]

  // Rinconero 166- → siguiente rinconero en pata
  if (refIsRinconero && drawingIsRinconero && referenceItem) {
    const nextRinconeroMatrix = composeMatrixModuleOnRinconeroLeg(refUnit, 0)
    if (nextRinconeroMatrix) return nextRinconeroMatrix
  }

  // Unidad recta → esquina modular
  if (drawingIsRinconero && !refIsRinconero && referenceItem) {
    const cornerMatrix = composeModularCornerOnUnitDrawMatrix(refUnit, drawingCatalog, offset)
    if (cornerMatrix) return cornerMatrix
  }

  // Rinconero → módulo recto (160, RELAX, …): mismo composer que collideItems.
  if (refIsRinconero && referenceItem && !drawingIsRinconero) {
    const width =
      moduleWidth > 0
        ? moduleWidth
        : defaultWidthForDrawCatalog(drawingCatalog) || Math.abs(offset) || 0
    const thickness =
      moduleThickness > 0 ? moduleThickness : defaultThicknessForDrawCatalog(drawingCatalog) || 0
    const side = snapSide ?? 'right'
    const snap = width > 0 ? composeStraightModuleSnapOnRinconero(refUnit, width, side, thickness) : null
    if (snap?.matrix) return snap.matrix
  }

  const offsetVector = toVector3([offset, heightOffset ?? 0, 0]).applyQuaternion(
    quaternion.normalize(),
  )
  const newPos = position.clone().add(offsetVector)
  return newMatrix.compose(newPos, quaternion, scale)
}

export function getRelativeMatrixOG(
  referenceMatrix: Matrix4,
  offset: number,
  referenceItem?: LinkedItem,
  heightOffset?: number,
) {
  const newMatrix = referenceMatrix.clone()

  const position = toVector3()
  const quaternion = toQuaternion()
  const scale = toVector3()
  newMatrix.decompose(position, quaternion, scale)

  const refIsRinconero = isRinconeroCatalog(referenceItem?.catalogID)

  if (refIsRinconero) {
    const offsetVector = toVector3([offset, 0, 0]).applyQuaternion(quaternion)
    const newPosition = position.clone().add(offsetVector)

    const rotationAdjustment = toQuaternion().setFromAxisAngle(toVector3([0, 1, 0]), -Math.PI / 2)
    const newQuaternion = quaternion.clone().multiply(rotationAdjustment)

    const thickness = referenceItem?.thickness || 0
    const zAxis = toVector3([0, 0, 1]).applyQuaternion(quaternion)
    newPosition.add(zAxis.multiplyScalar(thickness))

    return new Matrix4().compose(newPosition, newQuaternion, scale)
  }

  const offsetVector = toVector3([offset, heightOffset ?? 0, 0]).applyQuaternion(
    quaternion.normalize(),
  )
  const newPos = position.clone().add(offsetVector)

  return newMatrix.compose(newPos, quaternion, scale)
}
